import React, { useEffect, useRef, useState } from "react";
import "../Claim/ClaimApp.css";
import { ApiPaths } from "./../../Config/ApiPath";
import useAxiosHelper from "../../Common/AxiosHelper";
import { toastFailed, toastSuccess } from "../../Config/BasicInfo";
import { Landmark, Plus, X, ShieldCheck, Copy, Check, AlertTriangle } from "lucide-react";

// BEP20 (BNB Smart Chain) addresses are 0x + 40 hex characters
const BEP20_RE = /^0x[a-fA-F0-9]{40}$/;

const EditAccount = ({ embedded = false }) => {
  const [address, setAddress] = useState("");
  const [addressError, setAddressError] = useState("");
  const [loading, setLoading] = useState(false);
  const [accountDetails, setAccountDetails] = useState();
  const { AxiosGet, AxiosPost } = useAxiosHelper();
  const [showWallet, setShowWallet] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [copied, setCopied] = useState("");
  const otpRefs = useRef([]);

  useEffect(() => {
    FetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sheets: Escape closes, page behind stays put
  const sheetOpen = showWallet || showOtp;
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !otpLoading) {
        setShowOtp(false);
        setShowWallet(false);
      }
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [sheetOpen, otpLoading]);

  useEffect(() => {
    if (showOtp) setTimeout(() => otpRefs.current[0]?.focus(), 120);
  }, [showOtp]);

  async function FetchData() {
    try {
      setLoading(true);
      const response = await AxiosGet(ApiPaths.getWalletAddress);
      setAccountDetails(response?.web3Details || []);
    } catch (error) {
      console.log("Error fetching account details:", error);
      setAccountDetails((a) => a || []);
    } finally {
      setLoading(false);
    }
  }

  function resetError() {
    setAddressError("");
    setOtpError("");
  }

  async function handleSendOtp() {
    resetError();
    const value = address.trim();
    if (!value) {
      setAddressError("Address cannot be empty");
      return;
    }
    if (!BEP20_RE.test(value)) {
      setAddressError("That doesn't look like a BEP20 address (0x followed by 40 characters)");
      return;
    }
    try {
      setLoading(true);
      const responseOtp = await AxiosPost(ApiPaths.sendOTP, { action: "account" });
      toastSuccess(responseOtp?.message);
      setOtp(Array(6).fill(""));
      setShowOtp(true);
    } catch (e) {
      toastFailed(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  }

  async function setAccountFunc() {
    resetError();
    const code = otp.join("");
    if (code.length !== 6) {
      setOtpError("Enter the 6-digit code from your email");
      return;
    }
    try {
      setOtpLoading(true);
      const response = await AxiosPost(ApiPaths.setWalletAddress, {
        address: address.trim(),
        action: "account",
        otp: code,
      });
      toastSuccess(response?.message);
      setAddress("");
      setOtp(Array(6).fill(""));
      setShowOtp(false);
      setShowWallet(false);
      FetchData();
    } catch (e) {
      setOtpError(e?.response?.data?.message || e?.message || "Could not save the address");
    } finally {
      setOtpLoading(false);
    }
  }

  const setDigit = (i, v) => {
    setOtpError("");
    const clean = v.replace(/\D/g, "");
    setOtp((d) => {
      const next = [...d];
      if (!clean) {
        next[i] = "";
        return next;
      }
      clean
        .slice(0, 6 - i)
        .split("")
        .forEach((ch, k) => {
          next[i + k] = ch;
        });
      return next;
    });
    if (clean) otpRefs.current[Math.min(i + clean.length, 5)]?.focus();
  };

  const copy = async (a) => {
    try {
      await navigator.clipboard.writeText(a);
      setCopied(a);
      setTimeout(() => setCopied(""), 1400);
    } catch (e) {
      /* ignore */
    }
  };

  const list = Array.isArray(accountDetails) ? accountDetails : [];
  const firstLoad = accountDetails === undefined;

  return (
    <section className={`wdAccounts ${embedded ? "" : "wdStandalone"}`}>
      <div className="wdHistBar">
        <h2>Payout account</h2>
      </div>

      {firstLoad || (loading && !list.length && !showWallet) ? (
        <div className="wdTx wdSkel">
          <span className="a" />
          <span className="b" />
        </div>
      ) : list.length === 0 ? (
        <div className="wdEmpty">
          <span>
            <Landmark size={26} />
          </span>
          <b>No payout account yet</b>
          <small>Add the BEP20 USDT address where your withdrawals should be sent.</small>
          <button type="button" className="solid" onClick={() => setShowWallet(true)}>
            <Plus size={15} /> Add BEP20 address
          </button>
        </div>
      ) : (
        <div className="wdList">
          {list.map((x, i) => (
            <div key={x?.address || i} className="wdAccCard">
              <div className="wdAccCardTop">
                <span className="wdAccIcon">
                  <Landmark size={18} />
                </span>
                <span className="wdAccText">
                  <b>{(x?.chain || "BEP20").toUpperCase()} · USDT</b>
                  <small>Withdrawals are sent here</small>
                </span>
                <span className="wdStatus ok">
                  <Check size={12} /> Active
                </span>
              </div>
              <div className="wdAddr">
                <code>{x?.address}</code>
                <button type="button" onClick={() => copy(x?.address)} aria-label="Copy address">
                  {copied === x?.address ? <Check size={15} /> : <Copy size={15} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------- Add address ---------- */}
      {showWallet && !showOtp && (
        <div className="wdOverlay" onClick={() => setShowWallet(false)}>
          <div className="wdSheet" role="dialog" aria-modal="true" aria-labelledby="wdAddTitle" onClick={(e) => e.stopPropagation()}>
            <span className="wdHandle" aria-hidden="true" />
            <button type="button" className="wdClose" onClick={() => setShowWallet(false)} aria-label="Close">
              <X size={18} />
            </button>
            <h3 id="wdAddTitle">Add payout address</h3>
            <p className="wdSheetSub">BEP20 (BNB Smart Chain) · USDT</p>
            <label className="wdField">
              <span>Wallet address</span>
              <input
                type="text"
                placeholder="0x…"
                value={address}
                onChange={(e) => {
                  setAddressError("");
                  setAddress(e.target.value);
                }}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                className="mono"
              />
            </label>
            {addressError && (
              <p className="wdError">
                <AlertTriangle size={14} /> {addressError}
              </p>
            )}
            <div className="wdWarn">
              <AlertTriangle size={15} />
              <span>Double-check the address. Funds sent to a wrong or non-BEP20 address can't be recovered.</span>
            </div>
            <div className="wdSheetActions">
              <button type="button" className="wdBtn ghost" onClick={() => setShowWallet(false)}>
                Cancel
              </button>
              <button type="button" className="wdBtn primary" onClick={handleSendOtp} disabled={loading}>
                {loading ? <span className="wdSpin" /> : <ShieldCheck size={16} />} Send code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- OTP ---------- */}
      {showOtp && (
        <div className="wdOverlay" onClick={() => !otpLoading && setShowOtp(false)}>
          <div className="wdSheet" role="dialog" aria-modal="true" aria-labelledby="wdAccOtp" onClick={(e) => e.stopPropagation()}>
            <span className="wdHandle" aria-hidden="true" />
            <button type="button" className="wdClose" onClick={() => setShowOtp(false)} disabled={otpLoading} aria-label="Close">
              <X size={18} />
            </button>
            <h3 id="wdAccOtp">Verify it's you</h3>
            <p className="wdSheetSub">Enter the 6-digit code sent to your registered email to save this address.</p>
            <div className="wdAddr compact">
              <code>{address}</code>
            </div>
            <div className="wdOtp" role="group" aria-label="Verification code">
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  className={`${d ? "isFilled" : ""} ${otpError ? "isError" : ""}`}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  maxLength={6}
                  value={d}
                  aria-label={`Digit ${i + 1}`}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
                    if (e.key === "Enter") setAccountFunc();
                  }}
                  onFocus={(e) => e.target.select()}
                />
              ))}
            </div>
            {otpError && (
              <p className="wdError center">
                <AlertTriangle size={14} /> {otpError}
              </p>
            )}
            <div className="wdSheetActions">
              <button
                type="button"
                className="wdBtn ghost"
                onClick={() => {
                  setOtp(Array(6).fill(""));
                  setShowOtp(false);
                  resetError();
                }}
                disabled={otpLoading}
              >
                Cancel
              </button>
              <button type="button" className="wdBtn primary" onClick={setAccountFunc} disabled={otpLoading}>
                {otpLoading ? (
                  <>
                    <span className="wdSpin" /> Saving…
                  </>
                ) : (
                  "Save address"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default EditAccount;
